import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { listPendingApplications, listAdminOverview, decideApplication, listPendingVenues, decideVenueApplication, resendOnboardingConfirmation } from "@/lib/applications.functions";
import { getIsAdmin } from "@/lib/payments.functions";
import { ReserveHeader } from "@/components/ReserveHeader";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

export const Route = createFileRoute("/_authenticated/admin/")({
  head: () => ({ meta: [{ title: "Admin Console — The Hospitality Reserve" }] }),
  component: AdminHome,
});

type Tab = "applications" | "venue_applications" | "bartenders" | "venues";

type PendingVenue = {
  id: string;
  full_name: string | null;
  email: string | null;
  headline: string | null;
  business_name?: string | null;
  legal_trading_name?: string | null;
  companies_house_number?: string | null;
  business_email?: string | null;
  phone_number?: string | null;
  address?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  postcode?: string | null;
  country?: string | null;
  address_verified_at?: string | null;
  created_at: string;
};

function AdminHome() {
  const navigate = useNavigate();
  const checkAdmin = useServerFn(getIsAdmin);
  const loadPending = useServerFn(listPendingApplications);
  const loadOverview = useServerFn(listAdminOverview);
  const loadPendingVenues = useServerFn(listPendingVenues);
  const decide = useServerFn(decideApplication);
  const decideVenue = useServerFn(decideVenueApplication);
  const resend = useServerFn(resendOnboardingConfirmation);

  const [tab, setTab] = useState<Tab>("applications");
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState<Array<{ id: string; full_name: string | null; email: string | null; quiz_score: number | null; cv_signed_url: string | null }>>([]);
  const [pendingVenues, setPendingVenues] = useState<PendingVenue[]>([]);
  const [bartenders, setBartenders] = useState<Array<{ id: string; full_name: string | null; email: string | null; onboarding_status: string | null; quiz_score: number | null; created_at: string; cv_file_path: string | null }>>([]);
  const [venues, setVenues] = useState<Array<{ id: string; full_name: string | null; email: string | null; headline: string | null; created_at: string }>>([]);
  const [actingId, setActingId] = useState<string | null>(null);

  // Combined review dialog — supports both approve (with notes) and reject (with reason + notes).
  const [reviewing, setReviewing] = useState<{ venue: PendingVenue; mode: "accept" | "deny" } | null>(null);
  const [reviewNotes, setReviewNotes] = useState("");
  const [reviewReason, setReviewReason] = useState("");

  const refresh = async () => {
    setLoading(true);
    const [p, o, v] = await Promise.all([loadPending(), loadOverview(), loadPendingVenues()]);
    if ("error" in p) toast.error(p.error);
    else setPending(p.applications);
    if ("error" in o) toast.error(o.error);
    else { setBartenders(o.bartenders); setVenues(o.venues); }
    if ("error" in v) toast.error(v.error);
    else setPendingVenues(v.venues as PendingVenue[]);
    setLoading(false);
  };

  useEffect(() => {
    (async () => {
      const a = await checkAdmin();
      if (!a.isAdmin) { navigate({ to: "/dashboard", replace: true }); return; }
      await refresh();
    })();
  }, []);

  const handleDecide = async (id: string, decision: "accept" | "deny") => {
    setActingId(id);
    try {
      const r = await decide({ data: { applicant_id: id, decision } });
      if ("error" in r) throw new Error(r.error);
      toast.success(decision === "accept" ? "Application accepted." : "Application denied.");
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not complete action.");
    } finally {
      setActingId(null);
    }
  };

  const handleResend = async (id: string) => {
    setActingId(id);
    try {
      const r = await resend({ data: { subject_user_id: id } });
      if ("error" in r) throw new Error(r.error);
      toast.success("Confirmation email re-queued.");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not resend.");
    } finally {
      setActingId(null);
    }
  };

  const submitReview = async () => {
    if (!reviewing) return;
    const id = reviewing.venue.id;
    const mode = reviewing.mode;
    const notes = reviewNotes.trim();
    const reason = reviewReason.trim();
    setActingId(id);
    try {
      const r = await decideVenue({
        data: {
          venue_id: id,
          decision: mode,
          ...(reason ? { reason } : {}),
          ...(notes ? { admin_notes: notes } : {}),
        },
      });
      if ("error" in r) throw new Error(r.error);
      toast.success(mode === "accept" ? "Venue approved." : "Venue rejected.");
      setReviewing(null);
      setReviewNotes("");
      setReviewReason("");
      await refresh();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not complete action.");
    } finally {
      setActingId(null);
    }
  };

  const statusLabel = (s: string | null) => {
    switch (s) {
      case "pending_quiz": return "Pending CV upload";
      case "pending_cv_review": return "Pending CV review";
      case "pending_venue_review": return "Pending venue review";
      case "approved": return "Approved";
      case "rejected": return "Rejected";
      default: return s ?? "—";
    }
  };

  const tabs: { id: Tab; label: string; count: number }[] = [
    { id: "applications", label: "CV Applications", count: pending.length },
    { id: "venue_applications", label: "Venue Applications", count: pendingVenues.length },
    { id: "bartenders", label: "All Bartenders", count: bartenders.length },
    { id: "venues", label: "All Venues", count: venues.length },
  ];

  return (
    <div className="min-h-screen bg-background">
      <ReserveHeader role="admin" />
      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className="tracking-wide-luxury text-accent">Admin Console</p>
            <h1 className="mt-3 font-display text-3xl">The Hospitality Reserve</h1>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/admin/analytics" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Analytics</Link>
            <Link to="/admin/support" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Support inbox</Link>
            <Link to="/admin/disputes" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Disputes</Link>
            <Link to="/admin/timeline" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Timeline</Link>
            <Link to="/admin/payouts" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Payouts</Link>
           <Link to="/admin/rtw" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">RTW Review</Link>
          <Link to="/admin/veriff-log" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Veriff Audit Log</Link>
           <Link to="/admin/mixologists" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Mixologist Scrutiny</Link>
            <Link to="/admin/nearby-preview" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Nearby Preview</Link>
            <Link to="/admin/cron" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Cron Health</Link>
            <Link to="/admin/chat-moderation" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Chat Moderation</Link>
            <Link to="/admin/message-reports" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Reported Messages</Link>
           <Link to="/admin/incidents" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Incident Reports</Link>
           <Link to="/admin/insurance" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Insurance</Link>
           <Link to="/admin/hmrc-export" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">HMRC Export</Link>
          <Link to="/admin/suspensions" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Suspensions</Link>
          <Link to="/admin/tip-audit" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Tip audit</Link>
          <Link to="/admin/tip-reminders" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Tip reminders</Link>
          <Link to="/admin/action-log" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Action log</Link>
          <Link to="/admin/fee-tiers" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Fee tiers</Link>
          <Link to="/admin/audit-timeline" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Audit timeline</Link>
          <Link to="/admin/team" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Admin team</Link>
          <Link to="/admin/reconciliation" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Reconciliation</Link>
          <Link to="/admin/payout-ledger" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Payout ledger</Link>
          <Link to="/admin/compliance-reports" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Compliance reports</Link>
          <Link to="/admin/review-moderation" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Review moderation</Link>
          <Link to="/admin/ai-desk" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">AI desk</Link>
          <Link to="/admin/venue-verification" className="border border-border px-4 py-2 text-xs tracking-wide-luxury hover:border-accent">Venue verification</Link>
            <button
              onClick={async () => {
                const { supabase } = await import("@/integrations/supabase/client");
                await supabase.auth.signOut();
                window.location.href = "/auth";
              }}
              className="px-4 py-2 text-xs tracking-wide-luxury border border-destructive text-destructive hover:bg-destructive hover:text-white transition-colors"
            >
              Sign Out
            </button>
          </div>
        </div>
        <span className="gold-divider mt-4" />
        <p className="mt-4 text-sm text-muted-foreground">
          Review CV applications, monitor bartenders, and oversee venues from one place.
        </p>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-4">
          {tabs.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              className={`border p-5 text-left transition ${tab === t.id ? "border-accent bg-card" : "border-border bg-card/40 hover:border-accent/50"}`}
            >
              <p className="text-xs tracking-wide-luxury text-muted-foreground">{t.label}</p>
              <p className="mt-2 font-display text-3xl">{loading ? "—" : t.count}</p>
            </button>
          ))}
        </div>

        {loading ? (
          <p className="mt-12 text-center text-sm text-muted-foreground">Loading…</p>
        ) : tab === "applications" ? (
          <section className="mt-10">
            <div className="flex items-center justify-between">
              <h2 className="font-display text-2xl">Pending CV Applications</h2>
              <Link to="/admin/applications" className="text-xs tracking-wide-luxury text-accent hover:text-accent/80">
                Full review view →
              </Link>
            </div>
            {pending.length === 0 ? (
              <div className="mt-6 border border-border bg-card p-8 text-center">
                <p className="font-display text-lg">All caught up.</p>
                <p className="mt-2 text-sm text-muted-foreground">No applications waiting for review.</p>
              </div>
            ) : (
              <div className="mt-6 space-y-4">
                {pending.map((a) => (
                  <article key={a.id} className="border border-border bg-card p-5">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="font-display text-lg">{a.full_name || "Unnamed applicant"}</p>
                        <p className="text-xs text-muted-foreground">{a.email || "—"}</p>
                      </div>
                      <div className="flex gap-2">
                        {a.cv_signed_url && (
                          <a href={a.cv_signed_url} target="_blank" rel="noopener noreferrer"
                             className="border border-border px-3 py-2 text-xs tracking-wide-luxury hover:border-accent">
                            View CV
                          </a>
                        )}
                        <Button disabled={actingId === a.id} onClick={() => handleDecide(a.id, "accept")}
                          className="rounded-none bg-accent text-accent-foreground hover:bg-accent/90 tracking-wide-luxury">
                          Accept
                        </Button>
                        <Button disabled={actingId === a.id} variant="outline" onClick={() => handleDecide(a.id, "deny")}
                          className="rounded-none border-destructive text-destructive hover:bg-destructive/10 tracking-wide-luxury">
                          Deny
                        </Button>
                      </div>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </section>
        ) : tab === "venue_applications" ? (
          <section className="mt-10">
            <h2 className="font-display text-2xl">Pending Venue Applications</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Every venue must be approved before they can post shifts.
            </p>
            {pendingVenues.length === 0 ? (
              <div className="mt-6 border border-border bg-card p-8 text-center">
                <p className="font-display text-lg">All caught up.</p>
                <p className="mt-2 text-sm text-muted-foreground">No venues waiting for approval.</p>
              </div>
            ) : (
              <div className="mt-6 space-y-4">
                {pendingVenues.map((v) => {
                  const venueName = v.business_name || v.headline || v.full_name || "Unnamed venue";
                  const fullAddress = [v.address_line1, v.address_line2, v.city, v.postcode]
                    .filter(Boolean).join(", ") || v.address || "—";
                  return (
                    <article key={v.id} className="border border-border bg-card p-5">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="space-y-1">
                          <p className="font-display text-lg">{venueName}</p>
                          <p className="text-xs text-muted-foreground">Contact: {v.full_name || "—"}</p>
                          <p className="text-xs text-muted-foreground">Login: {v.email || "—"}</p>
                          <p className="text-xs text-muted-foreground">Business email: {v.business_email || "—"}</p>
                          <p className="text-xs text-muted-foreground">Phone: {v.phone_number || "—"}</p>
                          <p className="text-xs text-muted-foreground">
                            Legal name:{" "}
                            {v.legal_trading_name ? (
                              <a
                                className="underline underline-offset-2"
                                target="_blank"
                                rel="noopener noreferrer"
                                href={`https://find-and-update.company-information.service.gov.uk/search?q=${encodeURIComponent(
                                  v.companies_house_number || v.legal_trading_name,
                                )}`}
                              >
                                {v.legal_trading_name}
                                {v.companies_house_number ? ` · ${v.companies_house_number}` : ""}
                              </a>
                            ) : (
                              <span className="text-destructive">not provided</span>
                            )}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            Address: {fullAddress}
                            {v.address_verified_at && (
                              <span className="ml-2 text-accent">· verified</span>
                            )}
                          </p>
                          <p className="mt-2 text-xs tracking-wide-luxury text-muted-foreground">
                            Registered {new Date(v.created_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex gap-2 flex-wrap">
                          <Link to="/admin/onboarding-activity/$userId" params={{ userId: v.id }}
                            className="border border-border px-3 py-2 text-xs tracking-wide-luxury hover:border-accent self-center">
                            Activity
                          </Link>
                          <Button disabled={actingId === v.id} variant="outline" onClick={() => handleResend(v.id)}
                            className="rounded-none border-border text-xs tracking-wide-luxury">
                            Resend email
                          </Button>
                          <Button disabled={actingId === v.id}
                            onClick={() => { setReviewing({ venue: v, mode: "accept" }); setReviewNotes(""); setReviewReason(""); }}
                            className="rounded-none bg-accent text-accent-foreground hover:bg-accent/90 tracking-wide-luxury">
                            Approve
                          </Button>
                          <Button disabled={actingId === v.id} variant="outline"
                            onClick={() => { setReviewing({ venue: v, mode: "deny" }); setReviewNotes(""); setReviewReason(""); }}
                            className="rounded-none border-destructive text-destructive hover:bg-destructive/10 tracking-wide-luxury">
                            Reject
                          </Button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            )}
          </section>
        ) : tab === "bartenders" ? (
          <section className="mt-10">
            <h2 className="font-display text-2xl">All Bartenders</h2>
            {bartenders.length === 0 ? (
              <p className="mt-6 text-sm text-muted-foreground">No bartenders yet.</p>
            ) : (
              <div className="mt-6 overflow-x-auto border border-border bg-card">
                <table className="w-full text-sm">
                  <thead className="bg-muted/30 text-left text-xs tracking-wide-luxury text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3">Name</th>
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">Status</th>
                      <th className="px-4 py-3">Joined</th>
                      <th className="px-4 py-3 text-right">Activity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bartenders.map((b) => (
                      <tr key={b.id} className="border-t border-border">
                        <td className="px-4 py-3">
                          <Link to="/bartenders/$id" params={{ id: b.id }} className="hover:text-accent">
                            {b.full_name || "—"}
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{b.email || "—"}</td>
                        <td className="px-4 py-3">{statusLabel(b.onboarding_status)}</td>
                        <td className="px-4 py-3 text-muted-foreground">{new Date(b.created_at).toLocaleDateString()}</td>
                        <td className="px-4 py-3 text-right">
                          <Link to="/admin/onboarding-activity/$userId" params={{ userId: b.id }}
                            className="text-xs tracking-wide-luxury text-accent hover:text-accent/80">
                            View →
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        ) : (
          <section className="mt-10">
            <h2 className="font-display text-2xl">All Venues</h2>
            {venues.length === 0 ? (
              <p className="mt-6 text-sm text-muted-foreground">No venues yet.</p>
            ) : (
              <div className="mt-6 overflow-x-auto border border-border bg-card">
                <table className="w-full text-sm">
                  <thead className="bg-muted/30 text-left text-xs tracking-wide-luxury text-muted-foreground">
                    <tr>
                      <th className="px-4 py-3">Venue</th>
                      <th className="px-4 py-3">Contact</th>
                      <th className="px-4 py-3">Email</th>
                      <th className="px-4 py-3">Joined</th>
                    </tr>
                  </thead>
                  <tbody>
                    {venues.map((v) => (
                      <tr key={v.id} className="border-t border-border">
                        <td className="px-4 py-3">{v.headline || v.full_name || "—"}</td>
                        <td className="px-4 py-3 text-muted-foreground">{v.full_name || "—"}</td>
                        <td className="px-4 py-3 text-muted-foreground">{v.email || "—"}</td>
                        <td className="px-4 py-3 text-muted-foreground">{new Date(v.created_at).toLocaleDateString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}
      </main>

      <Dialog open={!!reviewing} onOpenChange={(o) => { if (!o) { setReviewing(null); setReviewNotes(""); setReviewReason(""); } }}>
        <DialogContent className="rounded-none border-border bg-card">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">
              {reviewing?.mode === "accept" ? "Approve" : "Reject"}{" "}
              {reviewing?.venue.business_name || reviewing?.venue.headline || reviewing?.venue.full_name || "this venue"}?
            </DialogTitle>
            <DialogDescription>
              {reviewing?.mode === "accept"
                ? "Add an optional internal note. The note is included in the welcome email if you fill it in."
                : "Optionally provide a reason (sent to the venue) and an internal admin note (audit trail only)."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {reviewing?.mode === "deny" && (
              <div className="space-y-2">
                <Label htmlFor="reason" className="text-xs tracking-wide-luxury text-muted-foreground">
                  Reason for the venue (shown to them)
                </Label>
                <Textarea
                  id="reason"
                  value={reviewReason}
                  onChange={(e) => setReviewReason(e.target.value.slice(0, 1000))}
                  maxLength={1000}
                  rows={3}
                  placeholder="e.g. Unable to verify business details. Please reapply with proof of trading address."
                  className="rounded-none"
                />
                <p className="text-xs text-muted-foreground text-right">{reviewReason.length}/1000</p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="notes" className="text-xs tracking-wide-luxury text-muted-foreground">
                Internal admin notes (audit trail)
              </Label>
              <Textarea
                id="notes"
                value={reviewNotes}
                onChange={(e) => setReviewNotes(e.target.value.slice(0, 2000))}
                maxLength={2000}
                rows={4}
                placeholder="e.g. Companies House verified · invoiced 22 Jun"
                className="rounded-none"
              />
              <p className="text-xs text-muted-foreground text-right">{reviewNotes.length}/2000</p>
            </div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => { setReviewing(null); setReviewNotes(""); setReviewReason(""); }}
              className="rounded-none tracking-wide-luxury">
              Cancel
            </Button>
            <Button onClick={submitReview} disabled={!!actingId}
              className={`rounded-none tracking-wide-luxury ${
                reviewing?.mode === "accept"
                  ? "bg-accent text-accent-foreground hover:bg-accent/90"
                  : "bg-destructive text-white hover:bg-destructive/90"
              }`}>
              {reviewing?.mode === "accept" ? "Confirm Approval" : "Confirm Rejection"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
